import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import { siteGraph, serializeJsonLd, type SchemaNode } from "@workspace/public-seo";

interface SeoHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  structuredData?: SchemaNode[];
}

const SITE_NAME = "Skoltend";
const DEFAULT_TITLE = "Skoltend｜香港學校招標資訊平台";
const DEFAULT_DESCRIPTION = "Skoltend 每日整合全港中小學最新招標資訊，涵蓋工程、IT、午膳及清潔等類別。支持18區精準篩選，立即免費註冊，輕鬆掌握學校採購與書面報價商機！";
const DEFAULT_KEYWORDS = "香港學校招標,學校投標,學校tender,香港tender,書面報價,學校書面報價,報價邀請,學校膳食招標,學校IT招標,學校工程招標,學校保安招標,學校清潔招標,學校維修招標,學校冷氣招標,學校消防招標,學校印刷招標,學校制服招標,學校保險招標,學校文具招標,學校傢俬招標,學校設備招標,Hong Kong school tender,school quotation Hong Kong";
const DEFAULT_OG_IMAGE = "https://www.skoltend.com/opengraph.jpg";
const BASE_URL = "https://www.skoltend.com";

export function SeoHead({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = DEFAULT_KEYWORDS,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  structuredData = [],
}: SeoHeadProps) {
  useEffect(() => {
    // The interactive page replaces the initial public-only graph. Do not
    // leave a previous route's JSON-LD behind after client-side navigation.
    document.querySelectorAll('script[data-seo-server="true"]').forEach(node => node.remove());
  }, [canonical]);
  const fullTitle = title ? `${title}｜${SITE_NAME}` : DEFAULT_TITLE;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : BASE_URL;

  return (
    <Helmet>
      <script id="skoltend-client-schema" type="application/ld+json">
        {serializeJsonLd(siteGraph(structuredData))}
      </script>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="zh_HK" />
      <meta property="og:site_name" content={SITE_NAME} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
    </Helmet>
  );
}
